import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  Select,
  Badge,
  Avatar,
  DataTable,
  ConfirmDialog,
  Alert,
  Modal,
  Skeleton,
  ErrorBoundary,
  EmptyState,
  FileUpload,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, StatusKey } from '@bbpos/ui';
import { userService } from '@/services/user.service';
import { locationService } from '@/services/location.service';
import { LocationAssignment } from '@/components/features/users/LocationAssignment';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import type { User, TenantRole } from '@/types/user';
import { ROLE_BADGE_VARIANT, ROLE_LEVELS } from '@/types/user';

const PANEL_CLASS =
  'rounded-2xl border border-slate-200/80 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)]';

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MODERATOR', label: 'Moderator' },
  { value: 'OWNER', label: 'Owner' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'CAPTAIN', label: 'Captain' },
  { value: 'CASHIER', label: 'Cashier' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

/**
 * MANAGER may view the staff list (ARCHITECTURE.md §10; GET /users is
 * @Roles('MANAGER')), but invite, edit and deactivate are @Roles('OWNER')
 * and the detail page (/users/:id) is OWNER-gated — so below OWNER the list
 * is read-only and offers none of those affordances.
 */
function useCanManageUsers(): boolean {
  const role = useAuthStore((s) => s.profile?.role ?? '');
  return (ROLE_LEVELS[role] ?? 0) >= ROLE_LEVELS.OWNER;
}

function toneClasses(tone: 'primary' | 'success' | 'warning' | 'danger' | 'info') {
  switch (tone) {
    case 'success':
      return { icon: 'bg-emerald-50 text-emerald-600', accent: 'from-emerald-500/20 to-emerald-500/0' };
    case 'warning':
      return { icon: 'bg-amber-50 text-amber-600', accent: 'from-amber-500/18 to-amber-500/0' };
    case 'danger':
      return { icon: 'bg-rose-50 text-rose-600', accent: 'from-rose-500/18 to-rose-500/0' };
    case 'info':
      return { icon: 'bg-sky-50 text-sky-600', accent: 'from-sky-500/18 to-sky-500/0' };
    default:
      return { icon: 'bg-indigo-50 text-indigo-600', accent: 'from-indigo-500/18 to-indigo-500/0' };
  }
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function KpiStat({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: string | number;
  tone: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  icon: React.ReactNode;
}) {
  const palette = toneClasses(tone);
  return (
    <div className={cn(PANEL_CLASS, 'relative overflow-hidden p-4')}>
      <div className={cn('absolute inset-x-0 top-0 h-12 bg-gradient-to-b', palette.accent)} />
      <div className="relative flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-xl font-semibold tracking-tight text-slate-950 tabular-nums">{value}</p>
        </div>
      </div>
    </div>
  );
}

function UsersIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-1.997M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function UserListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const searchRef = useRef<HTMLInputElement>(null);
  const profile = useAuthStore((s) => s.profile);
  const canManage = useCanManageUsers();

  const page = Number(searchParams.get('page') ?? '1');
  const roleFilter = searchParams.get('role') ?? '';
  const statusFilter = searchParams.get('is_active') ?? '';
  const searchQuery = searchParams.get('search') ?? '';

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [deactivateTarget, setDeactivateTarget] = useState<User | null>(null);

  // Keyboard shortcuts
  useKeyboardShortcut('/', useCallback(() => {
    searchRef.current?.focus();
  }, []));
  useKeyboardShortcut('n', useCallback(() => {
    if (canManage) navigate('/users/new');
  }, [navigate, canManage]));

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (searchInput) params.set('search', searchInput);
      else params.delete('search');
      params.set('page', '1');
      setSearchParams(params);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, roleFilter, statusFilter, searchQuery],
    queryFn: () =>
      userService.list({
        page,
        limit: 20,
        search: searchQuery || undefined,
        role: roleFilter || undefined,
        is_active: statusFilter || undefined,
      }),
    staleTime: 15_000,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => userService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeactivateTarget(null);
      toast({ title: 'User deactivated and sessions invalidated', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: err.message || 'Failed to deactivate user', variant: 'error' });
    },
  });

  function setFilter(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  }

  // Filter role options based on current user's level
  const userRole = profile?.role ?? '';
  const isPlatformRole = ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'].includes(userRole);
  const filteredRoleOptions = isPlatformRole
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((r) =>
        r.value === '' || ['OWNER', 'MANAGER', 'CAPTAIN', 'CASHIER'].includes(r.value),
      );

  // Compute summary KPIs from current data
  const users = data?.data ?? [];
  const totalCount = data?.meta?.total ?? 0;
  const activeCount = useMemo(() => users.filter((u) => u.is_active).length, [users]);
  const roleBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const u of users) {
      counts[u.role] = (counts[u.role] ?? 0) + 1;
    }
    return counts;
  }, [users]);
  const recentlyActive = useMemo(
    () => users.filter((u) => u.last_login_at && Date.now() - new Date(u.last_login_at).getTime() < 7 * 86400000).length,
    [users],
  );

  const currentUserLevel = ROLE_LEVELS[userRole] ?? 0;

  const allColumns: ColumnDef<User>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (_v, row) => {
        const identity = (
          <>
            <Avatar name={row.name} size="sm" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                {row.name}
              </p>
              <p className="text-xs text-slate-400 truncate">{row.email}</p>
            </div>
          </>
        );
        return canManage ? (
          <Link to={`/users/${row.id}`} className="flex items-center gap-3 group">
            {identity}
          </Link>
        ) : (
          <div className="flex items-center gap-3">{identity}</div>
        );
      },
    },
    {
      key: 'role',
      header: 'Role',
      render: (_v, row) => (
        <Badge variant={ROLE_BADGE_VARIANT[row.role] ?? ('draft' as StatusKey)}>
          {row.role.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'locations',
      header: 'Locations',
      render: (_v, row) => (
        <span className="text-sm text-slate-600">
          {row.locations?.length
            ? row.locations.map((l) => l.name).join(', ')
            : '\u2014'}
        </span>
      ),
    },
    {
      key: 'last_login_at',
      header: 'Last Active',
      render: (_v, row) => (
        <span className="text-sm text-slate-500 tabular-nums">
          {row.last_login_at ? new Date(row.last_login_at).toLocaleDateString('en-IN') : 'Never'}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_v, row) => (
        <Badge variant={row.is_active ? 'active' : 'draft'} dot>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (_v, row) => {
        const rowLevel = ROLE_LEVELS[row.role] ?? 0;
        const canDeactivate = row.is_active && rowLevel < currentUserLevel && row.id !== profile?.id;
        return (
          <div className="flex items-center gap-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/users/${row.id}`);
              }}
            >
              View
            </Button>
            {canDeactivate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeactivateTarget(row);
                }}
                className="text-rose-600 hover:text-rose-700"
              >
                Deactivate
              </Button>
            )}
          </div>
        );
      },
    },
  ];
  // Below OWNER there are no row actions to offer, so the column goes too.
  const columns = canManage ? allColumns : allColumns.filter((c) => c.key !== 'actions');

  if (error) {
    return (
      <Alert
        variant="danger"
        title="Failed to load users"
        description="An error occurred loading the user list. Please try refreshing."
        action={{ label: 'Retry', onClick: () => queryClient.invalidateQueries({ queryKey: ['users'] }) }}
      />
    );
  }

  const hasNoData = !isLoading && users.length === 0 && !searchQuery && !roleFilter && !statusFilter;

  return (
    <div className="animate-fade-in">
      {/* Summary KPIs */}
      {!hasNoData && !isLoading && (
        <section className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-6">
          <KpiStat label="Total members" value={totalCount} tone="primary" icon={<UsersIcon />} />
          <KpiStat label="Active" value={activeCount} tone="success" icon={<CheckCircleIcon />} />
          <KpiStat
            label="Roles in view"
            value={Object.keys(roleBreakdown).length}
            tone="info"
            icon={<ShieldIcon />}
          />
          <KpiStat
            label="Active this week"
            value={recentlyActive}
            tone="warning"
            icon={<ClockIcon />}
          />
        </section>
      )}

      {/* Filter Bar */}
      <div className={cn(PANEL_CLASS, 'p-4 mb-6')}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="w-full sm:w-72">
            <Input
              ref={searchRef}
              variant="search"
              placeholder="Search by name, email, phone..."
              value={searchInput}
              onChange={setSearchInput}
            />
          </div>
          <Select
            options={filteredRoleOptions}
            value={roleFilter}
            onChange={(v) => setFilter('role', Array.isArray(v) ? v[0] : v)}
            placeholder="Role"
            className="w-44"
          />
          <Select
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setFilter('is_active', Array.isArray(v) ? v[0] : v)}
            placeholder="Status"
            className="w-40"
          />
        </div>
      </div>

      {/* Empty state for no users */}
      {hasNoData ? (
        <EmptyState
          variant="page"
          title="No team members yet"
          description="Invite your first team member to get started"
          action={canManage ? { label: 'Invite User', onClick: () => navigate('/users/new') } : undefined}
          icon={
            <svg className="h-16 w-16 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          }
        />
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <DataTable<User>
              columns={columns}
              data={users}
              loading={isLoading}
              totalCount={data?.meta?.total}
              currentPage={page}
              pageSize={20}
              onPageChange={(p) => {
                const params = new URLSearchParams(searchParams);
                params.set('page', String(p));
                setSearchParams(params);
              }}
              emptyState={{
                title: 'No users found',
                description: 'Try adjusting your search or filters',
              }}
            />
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3">
            {users.map((user) => {
              const card = (
                <div className="flex items-center gap-3">
                  <Avatar name={user.name} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant={ROLE_BADGE_VARIANT[user.role] ?? ('draft' as StatusKey)} size="sm">
                      {user.role.replace(/_/g, ' ')}
                    </Badge>
                    <Badge variant={user.is_active ? 'active' : 'draft'} size="sm" dot>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              );
              return canManage ? (
                <Link
                  key={user.id}
                  to={`/users/${user.id}`}
                  className={cn(PANEL_CLASS, 'block p-4 transition-all duration-200 hover:shadow-lg')}
                >
                  {card}
                </Link>
              ) : (
                <div key={user.id} className={cn(PANEL_CLASS, 'block p-4')}>
                  {card}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Deactivate Confirm */}
      {deactivateTarget && (
        <ConfirmDialog
          open
          title="Deactivate User"
          description={`This will immediately log out ${deactivateTarget.name} and prevent future access. Their data will be preserved.`}
          confirmLabel="Deactivate"
          variant="danger"
          loading={deactivateMutation.isPending}
          onConfirm={() => deactivateMutation.mutate(deactivateTarget.id)}
          onCancel={() => setDeactivateTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Bulk Invite ──────────────────────────────────────────────────────────

/** Tenant roles that can be assigned via the bulk-invite CSV. */
const BULK_INVITE_ROLES = new Set<TenantRole>(['OWNER', 'MANAGER', 'CAPTAIN', 'CASHIER']);
const BULK_INVITE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Maximum users accepted per CSV batch. */
export const BULK_INVITE_MAX = 50;

export interface ParsedInviteRow {
  row: number;
  name: string;
  email: string;
  role: TenantRole;
  phone?: string;
  valid: boolean;
  errors?: string[];
}

/**
 * Parse a bulk-invite CSV (header: name,email,role,phone) into validated rows.
 * Only the first {@link BULK_INVITE_MAX} data rows are parsed. Each row is
 * validated for a name, a well-formed email, and a recognised tenant role;
 * phone is optional. Invalid rows are returned with `valid: false` and a list
 * of human-readable `errors` so they can be surfaced and skipped.
 */
export function parseBulkInviteCsv(text: string): ParsedInviteRow[] {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const nameIdx = headers.indexOf('name');
  const emailIdx = headers.indexOf('email');
  const roleIdx = headers.indexOf('role');
  const phoneIdx = headers.indexOf('phone');

  return lines.slice(1, 1 + BULK_INVITE_MAX).map((line, i) => {
    const cols = line.split(',').map((c) => c.trim());
    const errs: string[] = [];
    const name = nameIdx >= 0 ? cols[nameIdx] ?? '' : '';
    const email = emailIdx >= 0 ? (cols[emailIdx] ?? '').toLowerCase() : '';
    const roleRaw = roleIdx >= 0 ? (cols[roleIdx] ?? '').toUpperCase() : '';
    const phone = phoneIdx >= 0 ? cols[phoneIdx] || undefined : undefined;

    if (!name) errs.push('Name is required');
    if (!email) errs.push('Email is required');
    else if (!BULK_INVITE_EMAIL_RE.test(email)) errs.push('Invalid email');
    if (!roleRaw) errs.push('Role is required');
    else if (!BULK_INVITE_ROLES.has(roleRaw as TenantRole)) errs.push(`Invalid role "${roleRaw}"`);

    return {
      row: i + 2,
      name,
      email,
      role: roleRaw as TenantRole,
      phone,
      valid: errs.length === 0,
      errors: errs.length ? errs : undefined,
    };
  });
}

interface BulkInviteSummary {
  created: number;
  skipped: number;
  failed: { email: string; reason: string }[];
}

function BulkInviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedInviteRow[]>([]);
  const [summary, setSummary] = useState<BulkInviteSummary | null>(null);
  const [locationIds, setLocationIds] = useState<string[]>([]);

  // The CSV has no location column, so the batch is assigned one set of locations —
  // the API refuses a location-scoped role (CASHIER/MANAGER) without any.
  const { data: locData } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => locationService.list({ limit: 100 }),
    staleTime: 60_000,
    enabled: open,
  });
  const locations = locData?.data ?? [];

  const validRows = rows.filter((r) => r.valid);
  const invalidRows = rows.filter((r) => !r.valid);

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const result: BulkInviteSummary = { created: 0, skipped: 0, failed: [] };
      for (const r of validRows) {
        try {
          await userService.create({
            name: r.name,
            email: r.email,
            role: r.role,
            location_ids: locationIds,
            ...(r.phone ? { phone: r.phone } : {}),
          });
          result.created += 1;
        } catch (err) {
          // A duplicate email (409) is an expected skip, not a failure.
          if (err instanceof ApiError && err.statusCode === 409) {
            result.skipped += 1;
          } else {
            result.failed.push({
              email: r.email,
              reason: err instanceof Error ? err.message : 'Unknown error',
            });
          }
        }
      }
      return result;
    },
    onSuccess: (result) => {
      setSummary(result);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: `${result.created} invited · ${result.skipped} skipped · ${result.failed.length} failed`,
        variant: result.failed.length ? 'error' : 'success',
      });
    },
    onError: (err: Error) => {
      toast({ title: err.message || 'Bulk invite failed', variant: 'error' });
    },
  });

  function handleUpload(files: File[]) {
    const csvFile = files[0];
    if (!csvFile) return;
    if (!csvFile.name.toLowerCase().endsWith('.csv')) {
      toast({ title: 'Invalid file format. Please upload a .csv file.', variant: 'error' });
      return;
    }
    setSummary(null);
    setFileName(csvFile.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = (e.target?.result as string) ?? '';
      setRows(parseBulkInviteCsv(text));
    };
    reader.readAsText(csvFile);
  }

  function handleClose() {
    setFileName(null);
    setRows([]);
    setSummary(null);
    setLocationIds([]);
    onClose();
  }

  function handleReset() {
    setFileName(null);
    setRows([]);
    setSummary(null);
  }

  return (
    <Modal open={open} onClose={handleClose} title="Bulk Invite" variant="wide">
      <div className="space-y-4 p-2">
        <p className="text-sm text-slate-600">
          Upload a CSV file with columns:{' '}
          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
            name, email, role, phone
          </span>
        </p>

        {summary ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-emerald-50 p-4 text-center">
                <p className="text-2xl font-semibold text-emerald-600 tabular-nums">{summary.created}</p>
                <p className="text-xs font-medium text-emerald-700">Invited</p>
              </div>
              <div className="rounded-xl bg-amber-50 p-4 text-center">
                <p className="text-2xl font-semibold text-amber-600 tabular-nums">{summary.skipped}</p>
                <p className="text-xs font-medium text-amber-700">Skipped (existing)</p>
              </div>
              <div className="rounded-xl bg-rose-50 p-4 text-center">
                <p className="text-2xl font-semibold text-rose-600 tabular-nums">{summary.failed.length}</p>
                <p className="text-xs font-medium text-rose-700">Failed</p>
              </div>
            </div>
            {summary.failed.length > 0 && (
              <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3">
                <p className="text-xs font-semibold text-rose-700 mb-1">Failed rows</p>
                <ul className="space-y-0.5">
                  {summary.failed.map((f) => (
                    <li key={f.email} className="text-xs text-rose-600">
                      {f.email}: {f.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={handleReset}>
                Invite More
              </Button>
              <Button variant="primary" onClick={handleClose}>
                Done
              </Button>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <>
            <FileUpload accept=".csv" variant="dropzone" onUpload={handleUpload} />
            <p className="text-xs text-slate-400">
              Maximum {BULK_INVITE_MAX} users per batch. Existing emails will be skipped.
            </p>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {fileName && <Badge variant="info">{fileName}</Badge>}
              <Badge variant="active">{validRows.length} valid</Badge>
              {invalidRows.length > 0 && (
                <Badge variant="danger">{invalidRows.length} invalid (skipped)</Badge>
              )}
            </div>
            {invalidRows.length > 0 && (
              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 max-h-40 overflow-auto">
                <ul className="space-y-0.5">
                  {invalidRows.map((r) => (
                    <li key={r.row} className="text-xs text-amber-700">
                      Row {r.row}: {r.errors?.join('; ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">Assign to locations *</p>
              <LocationAssignment
                locations={locations}
                selected={locationIds}
                onChange={setLocationIds}
                disabled={inviteMutation.isPending}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={handleReset} disabled={inviteMutation.isPending}>
                Clear
              </Button>
              <Button
                variant="primary"
                onClick={() => inviteMutation.mutate()}
                disabled={
                  validRows.length === 0 || locationIds.length === 0 || inviteMutation.isPending
                }
              >
                {inviteMutation.isPending ? 'Inviting…' : `Invite ${validRows.length} User${validRows.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function UserListPage() {
  const navigate = useNavigate();
  const canManage = useCanManageUsers();
  const [showBulkInvite, setShowBulkInvite] = useState(false);

  return (
    <PageContainer
      title="Team Members"
      subtitle={
        canManage
          ? 'Manage staff accounts, roles, and location assignments'
          : 'Staff accounts, roles, and location assignments'
      }
      breadcrumbs={[{ label: 'Users' }]}
      actions={
        canManage ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setShowBulkInvite(true)}>
              Bulk Invite
            </Button>
            <Button variant="primary" onClick={() => navigate('/users/new')}>
              + Invite User
            </Button>
          </div>
        ) : undefined
      }
    >
      <ErrorBoundary>
        <UserListContent />
      </ErrorBoundary>

      {/* Bulk Invite Modal */}
      {canManage && (
        <BulkInviteModal open={showBulkInvite} onClose={() => setShowBulkInvite(false)} />
      )}
    </PageContainer>
  );
}
